import React, { useCallback, useEffect } from 'react';
import { useSceneContext } from '../contexts/SceneContext';

interface AnimationSystemProps {
    animationState: string;
    setAnimationState: (state: string) => void;
    addMessage: (msg: string) => void;
}

const AnimationSystem: React.FC<AnimationSystemProps> = ({ animationState, setAnimationState, addMessage }) => {
    const {
        sceneRef,
        rendererRef,
        cameraRef,
        droneCameraRef,
        controlsRef,
        componentsRef,
        particlesRef,
        droneRef,
        framePartsRef,
        animationFrameId
    } = useSceneContext();

    // 动画进度
    const animationProgress = React.useRef(0);
    const animationDuration = 2000; // 2秒
    const lastTrackedIndex = React.useRef<number>(-1);

    // 动画循环
    const animate = useCallback((time: number) => {
        if (controlsRef.current) controlsRef.current.update();

        const canvas = rendererRef.current?.domElement;
        if (!canvas) return;

        const width = canvas.clientWidth;
        const height = canvas.clientHeight;

        // 设置主视口
        rendererRef.current?.setViewport(0, 0, width, height);
        rendererRef.current?.setScissorTest(false);
        rendererRef.current?.render(sceneRef.current!, cameraRef.current!);

        // 循环往复运动的零件动画
        if (framePartsRef.current.length > 0) {
            const cycleDuration = 8000;
            const frameLengthX = 8;
            const frameLengthZ = 4;
            const halfLengthX = frameLengthX / 2;
            const halfLengthZ = frameLengthZ / 2;
            const partHeightOffset = 0.3 + 0.3 / 2;

            const getPos = (progress: number) => {
                let x = 0;
                let z = 0;
                if (progress < 0.25) {
                    x = -halfLengthX + (progress / 0.25) * frameLengthX;
                    z = halfLengthZ;
                } else if (progress < 0.5) {
                    x = halfLengthX;
                    z = halfLengthZ - ((progress - 0.25) / 0.25) * frameLengthZ;
                } else if (progress < 0.75) {
                    x = halfLengthX - ((progress - 0.5) / 0.25) * frameLengthX;
                    z = -halfLengthZ;
                } else {
                    x = -halfLengthX;
                    z = -halfLengthZ + ((progress - 0.75) / 0.25) * frameLengthZ;
                }
                return { x, y: halfLengthZ + partHeightOffset, z };
            };

            framePartsRef.current.forEach(part => {
                const progress = ((time / cycleDuration) + part.offset) % 1;
                const pos = getPos(progress);
                part.mesh.position.set(pos.x, pos.y, pos.z);
            });

            const index = Math.floor((time / 2000) % framePartsRef.current.length);
            const target = framePartsRef.current[index];

            if (droneRef.current && droneRef.current.group) {
                const droneGroup = droneRef.current.group;
                droneGroup.position.x += (target.mesh.position.x - droneGroup.position.x) * 0.1;
                droneGroup.position.y += (target.mesh.position.y + 2 - droneGroup.position.y) * 0.1;
                droneGroup.position.z += (target.mesh.position.z + 3 - droneGroup.position.z) * 0.1;

                droneGroup.children.forEach((obj: any) => {
                    const child = obj as any;
                    if (child.isMesh && child.geometry?.type === 'CylinderGeometry') {
                        child.rotation.y += 0.5;
                    }
                });
            }

            if (index !== lastTrackedIndex.current) {
                if (target.isCracked) {
                    addMessage('监测到缺损物体！');
                }
                lastTrackedIndex.current = index;
            }
        }

        // 渲染无人机视角窗口
        const viewPortWidth = Math.floor(width * 0.25);
        const viewPortHeight = Math.floor(height * 0.25);
        const viewPortPadding = 20;
        const viewPortX = width - viewPortWidth - viewPortPadding;
        const viewPortY = height - viewPortHeight - viewPortPadding;

        rendererRef.current?.setScissorTest(true);
        rendererRef.current?.setScissor(viewPortX, viewPortY, viewPortWidth, viewPortHeight);
        rendererRef.current?.setViewport(viewPortX, viewPortY, viewPortWidth, viewPortHeight);
        rendererRef.current?.clearDepth();

        if (droneCameraRef.current) {
            droneCameraRef.current.aspect = viewPortWidth / viewPortHeight;
            droneCameraRef.current.updateProjectionMatrix();
            rendererRef.current?.render(sceneRef.current!, droneCameraRef.current);
        }

        rendererRef.current?.setScissorTest(false);
        rendererRef.current?.setViewport(0, 0, width, height);

        // 处理数字化动画
        if (animationState === 'digitizing' || animationState === 'resetting') {
            const progress = (time - animationProgress.current) / animationDuration;
            let currentOpacity: number;
            let currentParticleOpacity: number;

            if (animationState === 'digitizing') {
                currentOpacity = Math.max(0, 1 - progress);
                currentParticleOpacity = Math.min(1, progress);
            } else {
                currentOpacity = Math.min(1, progress);
                currentParticleOpacity = Math.max(0, 1 - progress);
            }

            componentsRef.current.forEach(comp => {
                if (comp.solidMaterial) {
                    comp.solidMaterial.opacity = currentOpacity;
                    comp.solidMaterial.needsUpdate = true;
                    
                    if (animationState === 'digitizing' && currentOpacity <= 0.05) {
                        if (comp.mesh.material !== comp.wireframeMaterial) {
                            comp.mesh.material = comp.wireframeMaterial;
                            comp.mesh.material.opacity = 1;
                            comp.mesh.material.transparent = false;
                        }
                    } else if (animationState === 'resetting' && currentOpacity >= 0.95) {
                        if (comp.mesh.material !== comp.solidMaterial) {
                            comp.mesh.material = comp.solidMaterial;
                            comp.solidMaterial.opacity = 1;
                            comp.solidMaterial.transparent = false;
                        }
                    }
                }
            });

            if (particlesRef.current?.material) {
                particlesRef.current.material.opacity = currentParticleOpacity;
                particlesRef.current.material.needsUpdate = true;
            }

            if (progress >= 1) {
                setAnimationState('idle');
                componentsRef.current.forEach(comp => {
                    if (animationState === 'digitizing') {
                        comp.mesh.material = comp.wireframeMaterial;
                    } else {
                        comp.mesh.material = comp.solidMaterial;
                    }
                    if (comp.solidMaterial) {
                        comp.solidMaterial.opacity = 1;
                        comp.solidMaterial.transparent = false;
                    }
                });
                if (particlesRef.current?.material) {
                    particlesRef.current.material.opacity = (animationState === 'digitizing' ? 1 : 0);
                    particlesRef.current.material.transparent = (animationState === 'digitizing');
                }
            }
        }

        animationFrameId.current = requestAnimationFrame(animate);
    }, [animationState, setAnimationState, sceneRef, rendererRef, cameraRef, droneCameraRef, controlsRef, componentsRef, particlesRef, droneRef, framePartsRef, addMessage, animationFrameId]);

    useEffect(() => {
        animationFrameId.current = requestAnimationFrame(animate);
        return () => {
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
        };
    }, [animate, animationFrameId]);

    return null;
};

export default AnimationSystem; 